import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ClipboardScorePolicy } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";

import { CancellationCreditPenaltySubCard } from "../Adjustments/CancellationCreditPenaltySubCard";
import { type AdjustmentsSectionItem } from "../Adjustments/Section";
import { getCancellationItems, getTimelinessItems } from "../Adjustments/utils";

function getCreditItems(items: AdjustmentsSectionItem[]) {
  return items.filter(
    (
      item
    ): item is AdjustmentsSectionItem & {
      pointsWithCredit: number;
      pointsWithoutCredit: number;
    } =>
      item.hasCreditOption === true &&
      isDefined(item.pointsWithCredit) &&
      isDefined(item.pointsWithoutCredit)
  );
}

interface ScheduleChangesStepProps {
  readonly policy: ClipboardScorePolicy;
}

export function ScheduleChangesStep(props: ScheduleChangesStepProps) {
  const { policy } = props;

  const cancellationCreditItems = getCreditItems(
    getCancellationItems({
      policy,
      showCreditInfo: true,
      hasCredit: true,
    })
  );

  const latenessCreditItems = getCreditItems(
    getTimelinessItems({
      policy,
      showCreditInfo: true,
      hasCredit: true,
    })
  );

  return (
    <>
      <Text variant="h3" sx={{ fontWeight: 400 }}>
        When your schedule changes, you&apos;re covered
      </Text>
      <Stack spacing={5} sx={{ width: "100%", textAlign: "left" }}>
        {cancellationCreditItems.length > 0 && (
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <CbhIcon type="calendar-navigation" size="small" />
              <Text variant="h5">Same-day cancellation credit</Text>
            </Stack>
            {cancellationCreditItems.map((item) => (
              <CancellationCreditPenaltySubCard
                key={item.title}
                hasCredit
                title={item.title}
                pointsWithCredit={item.pointsWithCredit}
                pointsWithoutCredit={item.pointsWithoutCredit}
              />
            ))}
          </Stack>
        )}
        {latenessCreditItems.length > 0 && (
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <CbhIcon type="clock" size="small" />
              <Text variant="h5">Lateness credit</Text>
            </Stack>
            {latenessCreditItems.map((item) => (
              <CancellationCreditPenaltySubCard
                key={item.title}
                hasCredit
                title={item.title}
                pointsWithCredit={item.pointsWithCredit}
                pointsWithoutCredit={item.pointsWithoutCredit}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </>
  );
}
